import {
  AfterViewInit,
  Component,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { BaseListComponent } from '../base-list/base-list.component';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { MenuItem, MessageService, SharedModule } from 'primeng/api';
import { Tracking } from '../../../models/tracking';
import { TrackingStore } from '../../../stores/tracking/tracking.state';
import { UserStore } from '../../../stores/user/user.state';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { BadgeModule } from 'primeng/badge';
import { SidebarModule } from 'primeng/sidebar';
import { TableCaptionComponent } from '../../molecules/table-list-caption/table-list-caption.component';
import { TableModule } from 'primeng/table';
import { DatePipe, CommonModule } from '@angular/common';
import { addDays } from 'date-fns';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { saveAs } from 'file-saver';
import { ActiveUserStore } from '../../../stores/user/active-user.state';

const ROUTE_ANSICHT = /\/ansicht\/([a-f0-9]{24})/i;
const DWELL_SESSION_GAP_SECONDS = 30 * 60; // Pause > 30 Min. = Sitzungsende, wird verworfen

export interface ViewRankEntry {
  url: string;
  bookmarkId: string | null;
  bookmarkTitle: string;
  internalCount: number;
  externalCount: number;
  total: number;
  avgDwellSeconds: number | null;
  dwellSampleCount: number;
}

@Component({
  selector: 'app-tracking-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BadgeModule,
    SharedModule,
    SidebarModule,
    TableModule,
    DatePipe,
    ChartModule,
    DropdownModule,
    ButtonModule,
    CalendarModule,
    InputTextModule,
    TabViewModule,
    TooltipModule,
  ],
  templateUrl: './tracking-dashboard.component.html',
  styleUrl: './tracking-dashboard.component.scss',
})
export class TrackingDashboardComponent
  extends BaseListComponent<Tracking>
  implements OnInit, AfterViewInit
{
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-12',
    icon: 'fas fa-chart-line',
    headerText: 'System-Tracking & Statistiken',
  };

  readonly store = inject(TrackingStore);
  readonly userStore = inject(UserStore);
  readonly communeStore = inject(CommuneStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly activeUserStore = inject(ActiveUserStore);

  override menuItems: MenuItem[] = [];
  items: Tracking[] = [];

  // ── Filter ──────────────────────────────────────────────────────────────
  fromDate: Date | null = null;
  toDate: Date | null = null;
  filterType: string | null = null;
  filterContext: string | null = null;
  filterUserId: string | null = null;
  filterCommuneId: string | null = null;
  filterRouteUrl: string | null = null;
  filterLimit = 500;

  typeOptions = [
    { label: 'Alle Typen', value: null },
    { label: 'Routenwechsel', value: 'ROUTE_CHANGE' },
    { label: 'CRUD-Operationen', value: 'CRUD' },
    { label: 'Logins', value: 'LOGIN' },
  ];

  contextOptions = [
    { label: 'Intern & Extern', value: null },
    { label: 'Nur Intern', value: 'internal' },
    { label: 'Nur Extern (öffentlich)', value: 'external' },
  ];

  // ── Tab 0: Übersicht ─────────────────────────────────────────────────────
  lineData: any;
  lineOptions: any;
  barData: any;
  barOptions: any;
  pieData: any;
  pieOptions: any;

  // ── Tab 1: Ansichten-Ranking ──────────────────────────────────────────────
  viewRanking: ViewRankEntry[] = [];
  routeUrlOptions: { label: string; value: string | null }[] = [];
  overallAvgDwellSeconds: number | null = null;
  dwellSampleTotal = 0;
  viewRankingContext: string | null = null;
  viewRankingContextOptions = [
    { label: 'alle', value: null },
    { label: 'interne Nutzer:in', value: 'internal' },
    { label: 'externe Nutzer:in', value: 'external' },
  ];

  // ── Tab 2: Login-Analyse ──────────────────────────────────────────────────
  loginItems: Tracking[] = [];
  loginLineData: any;
  loginLineOptions: any;

  // ── Tab 3: Intern vs. Extern ──────────────────────────────────────────────
  internalItems: Tracking[] = [];
  externalItems: Tracking[] = [];
  internalExternalData: any;
  internalExternalOptions: any;

  // ── Tab 4: Aktivitäts-Heatmap ───────────────────────────────────────────
  readonly weekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  readonly hourLabels = Array.from({ length: 24 }, (_, h) => h);
  heatmapMatrix: number[][] = [];
  heatmapMax = 0;
  peakSlot: { day: string; hour: number; count: number } | null = null;

  // ── Tab 5: Top-Nutzer ────────────────────────────────────────────────────
  topUsers: { userId: string; count: number; percentage: number }[] = [];
  activeUsersCount = 0;

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private translate: TranslateService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    effect(() => {
      this.items = this.store.asArray();
      this.rebuildAllViews();
    });

    this.translate.setDefaultLang('en');
    this.translate.use('de');
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadFilteredData();
  }

  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadFilteredData(): void {
    const filters: any = {
      from: this.fromDate ? this.fromDate.getTime() : null,
      till: this.toDate ? addDays(this.toDate, 1).getTime() : null,
      type: this.filterType,
      context: this.filterContext,
      userId: this.filterUserId,
      communeId: this.isSuperAdmin()
        ? this.filterCommuneId
        : this.activeUserStore.commune(),
      limit: this.filterLimit,
    };
    this.store.loadFiltered(filters);
  }

  resetFilters(): void {
    this.fromDate = null;
    this.toDate = null;
    this.filterType = null;
    this.filterContext = null;
    this.filterUserId = null;
    this.filterCommuneId = null;
    this.filterRouteUrl = null;
    this.filterLimit = 500;
    this.loadFilteredData();
  }

  // ── Rebuild all tab data ──────────────────────────────────────────────────

  private rebuildAllViews(): void {
    this.buildRouteUrlOptions();
    this.updateOverviewCharts();
    this.buildViewRanking();
    this.buildLoginAnalysis();
    this.buildInternalExternalChart();
    this.buildHeatmap();
    this.buildTopUsers();
  }

  // Locally filtered items (route URL filter applied client-side after load)
  get filteredItems(): Tracking[] {
    if (!this.filterRouteUrl) return this.items;
    return this.items.filter(i => i.details?.url === this.filterRouteUrl);
  }

  // ── Tab 0: Übersicht charts ───────────────────────────────────────────────

  private updateOverviewCharts(): void {
    const data = this.filteredItems;
    if (!data.length) {
      this.lineData = this.barData = this.pieData = null;
      return;
    }

    // Activity over time
    const dateGroups: Record<string, number> = {};
    data.forEach(item => {
      if (item.timestamp) {
        const d = new Date(item.timestamp * 1000).toLocaleDateString('de-DE');
        dateGroups[d] = (dateGroups[d] || 0) + 1;
      }
    });
    const sortedDates = Object.keys(dateGroups).reverse();
    this.lineData = {
      labels: sortedDates,
      datasets: [
        {
          label: 'Aktivitäten',
          data: sortedDates.map(d => dateGroups[d]),
          fill: true,
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.3,
        },
      ],
    };
    this.lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };

    // Type distribution
    let routeCount = 0,
      crudCount = 0,
      loginCount = 0;
    data.forEach(i => {
      if (i.type === 'ROUTE_CHANGE') routeCount++;
      else if (i.type === 'CRUD') crudCount++;
      else if (i.type === 'LOGIN') loginCount++;
    });
    this.barData = {
      labels: ['Routenwechsel', 'CRUD-Aktionen', 'Logins'],
      datasets: [
        {
          label: 'Anzahl Events',
          backgroundColor: ['#10B981', '#F59E0B', '#3B82F6'],
          hoverBackgroundColor: ['#34D399', '#FBBF24', '#60A5FA'],
          borderRadius: {
            topLeft: 8,
            topRight: 8,
            bottomLeft: 0,
            bottomRight: 0,
          },
          borderSkipped: 'bottom',
          maxBarThickness: 56,
          data: [routeCount, crudCount, loginCount],
        },
      ],
    };
    this.barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24 } },
      plugins: {
        legend: { display: false },
        datalabels: {
          anchor: 'end',
          align: 'end',
          offset: 4,
          color: '#374151',
          font: { weight: 'bold', size: 13 },
          formatter: (value: number) => value,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0 },
          grace: '10%',
          grid: { color: 'rgba(0, 0, 0, 0.06)' },
        },
        x: { grid: { display: false } },
      },
    };

    // Commune distribution (super-admin only)
    if (this.isSuperAdmin()) {
      const communeGroups: Record<string, number> = {};
      data.forEach(item => {
        const name = this.getCommuneName(item.communeId);
        communeGroups[name] = (communeGroups[name] || 0) + 1;
      });
      const sorted = Object.keys(communeGroups).sort(
        (a, b) => communeGroups[b] - communeGroups[a]
      );
      const labels = sorted.slice(0, 5);
      const counts = labels.map(n => communeGroups[n]);
      if (sorted.length > 5) {
        labels.push('Andere');
        counts.push(sorted.slice(5).reduce((s, n) => s + communeGroups[n], 0));
      }
      this.pieData = {
        labels,
        datasets: [
          {
            data: counts,
            backgroundColor: [
              '#3B82F6',
              '#10B981',
              '#F59E0B',
              '#EF4444',
              '#8B5CF6',
              '#6B7280',
            ],
          },
        ],
      };
      this.pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { boxWidth: 12, usePointStyle: true },
          },
        },
      };
    }
  }

  // ── Tab 1: Ansichten-Ranking ──────────────────────────────────────────────

  private buildRouteUrlOptions(): void {
    const urls = Array.from(
      new Set(
        this.items
          .filter(i => i.type === 'ROUTE_CHANGE' && i.details?.url)
          .map(i => i.details!.url as string)
      )
    ).sort();

    this.routeUrlOptions = [
      { label: 'Alle Routen', value: null },
      ...urls.map(url => ({ label: this.shortUrl(url), value: url })),
    ];
  }

  onViewRankingContextChange(): void {
    this.buildViewRanking();
  }

  private buildViewRanking(): void {
    let routeItems = this.items.filter(
      i =>
        i.type === 'ROUTE_CHANGE' &&
        !!i.details?.url &&
        (!!i.details?.bookmarkId || this.isAnsichtUrl(i.details.url as string))
    );

    if (this.viewRankingContext === 'internal') {
      routeItems = routeItems.filter(
        i => !i.isPublic && i.context !== 'external'
      );
    } else if (this.viewRankingContext === 'external') {
      routeItems = routeItems.filter(
        i => i.isPublic || i.context === 'external'
      );
    }

    const map: Record<string, ViewRankEntry> = {};

    routeItems.forEach(item => {
      const url = item.details!.url as string;
      const bookmarkId =
        item.details?.bookmarkId || this.extractBookmarkId(url);

      if (!map[url]) {
        map[url] = {
          url,
          bookmarkId,
          bookmarkTitle: bookmarkId
            ? this.resolveBookmarkTitle(bookmarkId)
            : this.shortUrl(url),
          internalCount: 0,
          externalCount: 0,
          total: 0,
          avgDwellSeconds: null,
          dwellSampleCount: 0,
        };
      }

      if (item.isPublic || item.context === 'external') {
        map[url].externalCount++;
      } else {
        map[url].internalCount++;
      }
      map[url].total++;
    });

    this.applyDwellTimes(map, routeItems);

    this.viewRanking = Object.values(map).sort((a, b) => b.total - a.total);
  }

  // Approximate Verweildauer aus der Zeitdifferenz aufeinanderfolgender
  // ROUTE_CHANGE-Events desselben Nutzers. Eingeloggte Nutzer werden über
  // ihre echte userId gruppiert, anonyme Besucher über eine im Browser
  // persistierte, zufällige Visitor-ID (kein Rückschluss auf die Person
  // möglich). Fehlt eine Kennung (z.B. sehr alte Events vor Einführung der
  // Visitor-ID), wird das Event von der Berechnung ausgeschlossen.
  private applyDwellTimes(
    map: Record<string, ViewRankEntry>,
    routeItems: Tracking[]
  ): void {
    const byUser: Record<string, Tracking[]> = {};
    routeItems.forEach(item => {
      if (!item.userId || !item.timestamp) return;
      (byUser[item.userId] ||= []).push(item);
    });

    const dwellSumByUrl: Record<string, number> = {};
    const dwellCountByUrl: Record<string, number> = {};
    let grandSum = 0;
    let grandCount = 0;

    Object.values(byUser).forEach(userItems => {
      const sorted = [...userItems].sort((a, b) => a.timestamp! - b.timestamp!);
      for (let i = 0; i < sorted.length - 1; i++) {
        const cur = sorted[i];
        const next = sorted[i + 1];
        const delta = next.timestamp! - cur.timestamp!;
        if (delta <= 0 || delta > DWELL_SESSION_GAP_SECONDS) continue;

        const url = cur.details?.url as string;
        if (!url) continue;

        dwellSumByUrl[url] = (dwellSumByUrl[url] || 0) + delta;
        dwellCountByUrl[url] = (dwellCountByUrl[url] || 0) + 1;
        grandSum += delta;
        grandCount++;
      }
    });

    Object.keys(map).forEach(url => {
      const count = dwellCountByUrl[url] || 0;
      map[url].dwellSampleCount = count;
      map[url].avgDwellSeconds = count ? dwellSumByUrl[url] / count : null;
    });

    this.dwellSampleTotal = grandCount;
    this.overallAvgDwellSeconds = grandCount ? grandSum / grandCount : null;
  }

  // ── Tab 2: Login-Analyse ──────────────────────────────────────────────────

  private buildLoginAnalysis(): void {
    this.loginItems = this.items.filter(i => i.type === 'LOGIN');

    if (!this.loginItems.length) {
      this.loginLineData = null;
      return;
    }

    const dateGroups: Record<string, number> = {};
    this.loginItems.forEach(item => {
      if (item.timestamp) {
        const d = new Date(item.timestamp * 1000).toLocaleDateString('de-DE');
        dateGroups[d] = (dateGroups[d] || 0) + 1;
      }
    });
    const sortedDates = Object.keys(dateGroups).reverse();
    this.loginLineData = {
      labels: sortedDates,
      datasets: [
        {
          label: 'Logins',
          data: sortedDates.map(d => dateGroups[d]),
          fill: true,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.3,
        },
      ],
    };
    this.loginLineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };
  }

  // ── Tab 3: Intern vs. Extern ──────────────────────────────────────────────

  private buildInternalExternalChart(): void {
    const routeItems = this.items.filter(i => i.type === 'ROUTE_CHANGE');
    this.internalItems = routeItems.filter(
      i => !i.isPublic && i.context !== 'external'
    );
    this.externalItems = routeItems.filter(
      i => i.isPublic || i.context === 'external'
    );

    if (!routeItems.length) {
      this.internalExternalData = null;
      return;
    }

    const internalByDate: Record<string, number> = {};
    const externalByDate: Record<string, number> = {};

    routeItems.forEach(item => {
      if (!item.timestamp) return;
      const d = new Date(item.timestamp * 1000).toLocaleDateString('de-DE');
      if (item.isPublic || item.context === 'external') {
        externalByDate[d] = (externalByDate[d] || 0) + 1;
      } else {
        internalByDate[d] = (internalByDate[d] || 0) + 1;
      }
    });

    const allDates = Array.from(
      new Set([...Object.keys(internalByDate), ...Object.keys(externalByDate)])
    ).sort((a, b) => {
      const parse = (s: string) => {
        const [day, month, year] = s.split('.').map(Number);
        return new Date(year, month - 1, day).getTime();
      };
      return parse(a) - parse(b);
    });

    this.internalExternalData = {
      labels: allDates,
      datasets: [
        {
          label: 'Intern (eingeloggt)',
          data: allDates.map(d => internalByDate[d] || 0),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Extern (öffentlicher Atlas)',
          data: allDates.map(d => externalByDate[d] || 0),
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          fill: true,
          tension: 0.3,
        },
      ],
    };
    this.internalExternalOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'top' } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };
  }

  // ── Tab 4: Aktivitäts-Heatmap ────────────────────────────────────────────

  private buildHeatmap(): void {
    const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
    let max = 0;
    let peak: { day: string; hour: number; count: number } | null = null;

    this.filteredItems.forEach(item => {
      if (!item.timestamp) return;
      const date = new Date(item.timestamp * 1000);
      const dayIndex = (date.getDay() + 6) % 7; // Mo=0 … So=6
      const hour = date.getHours();
      matrix[dayIndex][hour]++;
      if (matrix[dayIndex][hour] > max) {
        max = matrix[dayIndex][hour];
        peak = {
          day: this.weekdayLabels[dayIndex],
          hour,
          count: matrix[dayIndex][hour],
        };
      }
    });

    this.heatmapMatrix = matrix;
    this.heatmapMax = max;
    this.peakSlot = peak;
  }

  heatmapCellIntensity(count: number): number {
    if (!this.heatmapMax) return 0;
    return count / this.heatmapMax;
  }

  // ── Tab 5: Top-Nutzer ─────────────────────────────────────────────────────

  private buildTopUsers(): void {
    // Only registered/logged-in traffic — anonymous public visits now carry a
    // per-browser visitor id (for dwell-time session grouping) but are not
    // "Nutzer" in the sense of this leaderboard.
    const counts: Record<string, number> = {};
    this.filteredItems.forEach(item => {
      if (item.isPublic) return;
      const id = item.userId || 'anonym';
      counts[id] = (counts[id] || 0) + 1;
    });

    const sorted = Object.entries(counts)
      .filter(([id]) => id !== 'anonym')
      .sort((a, b) => b[1] - a[1]);

    const max = sorted.length ? sorted[0][1] : 0;
    this.topUsers = sorted.slice(0, 10).map(([userId, count]) => ({
      userId,
      count,
      percentage: max ? (count / max) * 100 : 0,
    }));
    this.activeUsersCount = sorted.length;
  }

  userInitials(userId: string): string {
    return userId.slice(0, 2).toUpperCase();
  }

  // ── Aktive Filter-Chips ───────────────────────────────────────────────────

  get activeFilterChips(): { key: string; label: string }[] {
    const chips: { key: string; label: string }[] = [];
    if (this.fromDate) {
      chips.push({
        key: 'fromDate',
        label: `Von: ${this.fromDate.toLocaleDateString('de-DE')}`,
      });
    }
    if (this.toDate) {
      chips.push({
        key: 'toDate',
        label: `Bis: ${this.toDate.toLocaleDateString('de-DE')}`,
      });
    }
    if (this.filterType) {
      const opt = this.typeOptions.find(o => o.value === this.filterType);
      chips.push({
        key: 'filterType',
        label: `Typ: ${opt?.label ?? this.filterType}`,
      });
    }
    if (this.filterContext) {
      const opt = this.contextOptions.find(o => o.value === this.filterContext);
      chips.push({
        key: 'filterContext',
        label: `Kontext: ${opt?.label ?? this.filterContext}`,
      });
    }
    if (this.filterUserId) {
      const opt = this.getUserOptions().find(
        o => o.value === this.filterUserId
      );
      chips.push({
        key: 'filterUserId',
        label: `Benutzer: ${opt?.label ?? this.filterUserId}`,
      });
    }
    if (this.filterCommuneId) {
      chips.push({
        key: 'filterCommuneId',
        label: `Kommune: ${this.getCommuneName(this.filterCommuneId)}`,
      });
    }
    if (this.filterRouteUrl) {
      chips.push({
        key: 'filterRouteUrl',
        label: `Route: ${this.shortUrl(this.filterRouteUrl)}`,
      });
    }
    return chips;
  }

  removeFilter(key: string): void {
    (this as any)[key] = null;
    this.loadFilteredData();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  isSuperAdmin(): boolean {
    return !!this.activeUserStore.user()?.isSuperAdmin;
  }

  getCommuneName(communeId: string): string {
    const commune = this.communeStore.getById(communeId);
    return commune ? commune.name : communeId || 'N/A';
  }

  getUserOptions(): any[] {
    return [
      { label: 'Alle Benutzer', value: null },
      ...this.userStore.asArray().map(u => ({
        label: `${u.firstName} ${u.lastName} (${u.username})`,
        value: u._id,
      })),
    ];
  }

  getCommuneOptions(): any[] {
    return [
      { label: 'Alle Kommunen', value: null },
      ...this.communeStore
        .asArray()
        .map(c => ({ label: c.name, value: c._id })),
    ];
  }

  private extractBookmarkId(url: string): string | null {
    const m = ROUTE_ANSICHT.exec(url);
    return m ? m[1] : null;
  }

  resolveBookmarkTitle(bookmarkId: string): string {
    const bm = this.bookmarkStore.getById(bookmarkId);
    return bm ? bm.title : `Ansicht (…${bookmarkId.slice(-6)})`;
  }

  shortUrl(url: string): string {
    return url.split('?')[0].replace(/\/atlas(?:-intern)?\/[^/]+/, '/atlas/…');
  }

  isAnsichtUrl(url: string): boolean {
    return ROUTE_ANSICHT.test(url);
  }

  formatDuration(seconds: number | null): string {
    if (seconds === null || !isFinite(seconds)) return '–';
    const s = Math.round(seconds);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return m === 0 ? `${rem}s` : `${m}m ${rem}s`;
  }

  exportCSV(): void {
    const headers = [
      'ID',
      'Kommune',
      'Pseudonym',
      'Typ',
      'Kontext',
      'Öffentlich',
      'Aktion',
      'Kollektion',
      'URL',
      'Bookmark-ID',
      'Zeitstempel',
    ];
    const rows = this.filteredItems.map(item => [
      item._id,
      this.getCommuneName(item.communeId),
      item.userId || 'Anonym',
      item.type,
      item.context || '',
      item.isPublic ? 'Ja' : 'Nein',
      item.action,
      item.targetCollection || '',
      item.details?.url || '',
      item.details?.bookmarkId || '',
      item.timestamp
        ? new Date(item.timestamp * 1000).toLocaleString('de-DE')
        : '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(r =>
        r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    saveAs(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
      `hyperion-tracking-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }
}
